/**
 * CountryWidget component.
 *
 * A select widget built on top of react-select (like SelectAutoComplete) that
 * is prefilled with a static list of all countries in the world, with
 * type-ahead search. Stores the country name as the value; single-select by
 * default, set `isMulti` for a multi-select.
 *
 * @module components/Widgets/CountryWidget/CountryWidget
 */

import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import {
  Option,
  ClearIndicator,
  DropdownIndicator,
  MultiValueContainer,
  selectTheme,
  customSelectStyles,
  MenuList,
} from '@plone/volto/components/manage/Widgets/SelectStyling';

import clearSVG from '@plone/volto/icons/clear.svg';

import countries from './countries';

const messages = defineMessages({
  select: {
    id: 'Select…',
    defaultMessage: 'Select…',
  },
});

const countryOptions = countries.map((name) => ({ value: name, label: name }));

// Use the same cross icon as every other clear button instead of
// react-select's built-in one.
const MultiValueRemove = (props) => (
  <div {...props.innerProps}>
    <Icon name={clearSVG} size="24px" className="close" />
  </div>
);

const CountryWidget = (props) => {
  const {
    id,
    title,
    value,
    onChange,
    onBlur,
    isDisabled,
    disabled,
    required,
    placeholder,
    isMulti,
    reactSelect,
  } = props;

  const intl = useIntl();
  const Select = reactSelect.default;

  const selectedValue = isMulti
    ? countryOptions.filter((option) => (value || []).includes(option.value))
    : countryOptions.find((option) => option.value === value) || null;

  return (
    <FormFieldWrapper {...props}>
      <Select
        id={`field-${id}`}
        name={id}
        aria-label={title || undefined}
        isDisabled={isDisabled || disabled}
        isMulti={isMulti}
        isClearable={!required}
        isSearchable
        menuShouldScrollIntoView={false}
        menuPlacement="auto"
        maxMenuHeight={1600}
        className="react-select-container"
        classNamePrefix="react-select"
        options={countryOptions}
        styles={customSelectStyles}
        theme={selectTheme}
        components={{
          MenuList,
          MultiValueContainer,
          MultiValueRemove,
          ClearIndicator,
          DropdownIndicator,
          Option,
        }}
        value={selectedValue}
        placeholder={placeholder ?? intl.formatMessage(messages.select)}
        onBlur={() => onBlur && onBlur(id, value)}
        onChange={(selected) => {
          if (isMulti) {
            onChange(id, selected ? selected.map((option) => option.value) : []);
          } else {
            onChange(id, selected ? selected.value : undefined);
          }
        }}
      />
    </FormFieldWrapper>
  );
};

CountryWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  wrapped: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  placeholder: PropTypes.string,
};

CountryWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  isMulti: false,
};

export default injectLazyLibs(['reactSelect'])(CountryWidget);
